/** Tiny networking helpers: a status-carrying HTTP error + exponential backoff. */

export class HttpError extends Error {
  readonly status: number;
  readonly bodySnippet: string;
  constructor(status: number, bodySnippet = "") {
    super(`HTTP ${status}${bodySnippet ? `: ${bodySnippet}` : ""}`);
    this.name = "HttpError";
    this.status = status;
    this.bodySnippet = bodySnippet;
  }
}

/** Wikidata throttles hard; these statuses are worth a retry. */
export function isRetryableStatus(status: number): boolean {
  return status === 429 || status === 500 || status === 502 || status === 503 || status === 504;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface RetryOptions {
  retries?: number;
  baseMs?: number;
  retryable?: (err: unknown) => boolean;
}

function defaultRetryable(err: unknown): boolean {
  if (err instanceof HttpError) return isRetryableStatus(err.status);
  // Network-level failures (fetch throws TypeError) are transient too.
  return err instanceof TypeError;
}

export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const retries = opts.retries ?? 4;
  const baseMs = opts.baseMs ?? 400;
  const retryable = opts.retryable ?? defaultRetryable;
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === retries || !retryable(err)) throw err;
      // Exponential backoff with jitter.
      await sleep(baseMs * 2 ** attempt * (0.6 + Math.random() * 0.8));
    }
  }
  throw lastErr;
}
